// Side panel (desktop ≥ lg) — выезжающая панель оболочки (e2).
// Push-режим: main сжимается на ширину panel (margin-right).
// Открытие: translateX (не width) — контент всегда в финальной ширине, карточки
// не reflow'ятся на промежуточных ширинах. Spec: notifications.panel.desktop.md.
// Источники: useSidePanelStore.kind = 'tutor' | 'notifications' | null.
// Notifications-kind наполняется реальными данными — NotificationPanel поверх
// PowerSync stream `user_notifications`. Дизайн-язык «Картотека» §8.A / §8.V.

import { DUR_BASE_MS } from "@studyhub/ui";
import { forwardRef, useEffect, useMemo, useState } from "react";
import { adaptNotification } from "@/components/notifications/notification-adapter";
import type { NotificationItemData } from "@/components/notifications/notification-item";
import { NotificationPanel } from "@/components/notifications/notification-panel";
import { useSession } from "@/hooks/use-session";
import { cn } from "@/lib/cn";
import { useNotificationsListQuery } from "@/lib/powersync/queries/use-notifications-query";
import { usePowerSyncLoading } from "@/lib/powersync/query-shim";
import type { ActiveSidePanel } from "./_layout-state";
import { useShellGeometry } from "./_panel-geometry";
import { PanelResizeHandle } from "./panel-resize-handle";

interface SidePanelProps {
	activePanel: ActiveSidePanel;
	onClose: () => void;
}

export const SidePanel = forwardRef<HTMLElement, SidePanelProps>(function SidePanel(
	{ activePanel, onClose },
	ref,
) {
	// В main-layout SidePanel рендерится только для kind ≠ 'tutor' —
	// единственный оставшийся kind = 'notifications'.
	const isOpen = activePanel === "notifications";

	// Контент держим смонтированным на время slide-out, иначе панель уезжает пустой.
	// Открытие: sync setState в render — тот же кадр, что и translate-x-0.
	// Закрытие: таймер на DUR_BASE_MS (0 при reduced-motion).
	const [showContent, setShowContent] = useState(isOpen);
	if (isOpen && !showContent) {
		setShowContent(true);
	}
	useEffect(() => {
		if (isOpen) return;
		const reduced =
			typeof window !== "undefined" &&
			window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		const delay = reduced ? 0 : DUR_BASE_MS;
		const timer = window.setTimeout(() => setShowContent(false), delay);
		return () => window.clearTimeout(timer);
	}, [isOpen]);

	const { user } = useSession();
	// Пока панель на экране (в т.ч. slide-out) — не гасим query, иначе список
	// обнулится mid-animation.
	const { data: rows, isLoading: queryLoading } = useNotificationsListQuery(
		showContent ? user?.id : undefined,
	);
	// Гейт против вечного скелетона: если локальная БД не отвечает — по таймауту
	// показываем «нет уведомлений», а не бесконечную загрузку. Считает его
	// `usePowerSyncLoading`; вручную гасить загрузку флагом первичного синка
	// нельзя — он не про этот запрос (см. `use-powersync-ready.ts`).
	const isLoading = usePowerSyncLoading(queryLoading);

	const items: NotificationItemData[] = useMemo(() => (rows ?? []).map(adaptNotification), [rows]);

	// Ширина — из общей геометрии оболочки (паритет с панелью репетитора + ресайз
	// мышью): на узком desktop панель ужимается до минимума вместо того, чтобы
	// вытеснять контент. Панель всегда в финальной ширине; open/close только
	// сдвигает её translateX.
	const panelWidth = useShellGeometry().panelWidth;

	return (
		<aside
			ref={ref}
			aria-hidden={!isOpen}
			inert={!isOpen ? true : undefined}
			data-panel={activePanel ?? "closed"}
			style={{ width: panelWidth }}
			className={cn(
				"fixed right-0 top-0 bottom-0 z-(--sh-z-chrome) hidden lg:flex flex-col",
				// Боковые панели — белый лист (bg-card); серый canvas остаётся у центра.
				"border-l border-border bg-card",
				"transition-transform duration-base ease-out motion-reduce:transition-none",
				isOpen ? "translate-x-0" : "translate-x-full pointer-events-none",
			)}
		>
			{showContent && (
				<>
					<PanelResizeHandle />
					<NotificationPanel items={items} isLoading={isLoading} onClose={onClose} />
				</>
			)}
		</aside>
	);
});
